import { isDifferential } from "@corca/core";

import type { OperatorNodeModel } from "@/models/nodes/OperatorNodeModel";
import type { OperatorSymbolNodeModel } from "@/models/nodes/OperatorSymbolNodeModel";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeJson, OperatorTypeValue } from "@corca/data";

export function isSign(node?: NodeModel | null): node is Maybe<OperatorSymbolNodeModel> {
  return node?.type === "operatorSymbol" && (node.value === "+" || node.value === "-");
}

export function isRelation(node?: NodeModel | null): node is Maybe<OperatorSymbolNodeModel> {
  return node?.type === "operatorSymbol" && !!node.semanticTypes?.includes("relation");
}

export function isEqualsSign(node?: NodeModel | null): node is Maybe<OperatorSymbolNodeModel> {
  return node?.type === "operatorSymbol" && node.value === "=";
}

type OperatorOf<T, OT extends OperatorTypeValue> = T extends { type: "operator" }
  ? T & (T extends NodeModel ? OperatorNodeModel<OT> : { props: { operatorType: OT } })
  : never;

export function isOperator<T extends NodeModel | NodeJson, OT extends OperatorTypeValue>(
  node?: T | null,
  operatorType?: OT,
): node is OperatorOf<T, OT> {
  return node?.type === "operator" && node.props.operatorType === operatorType;
}

export function isPowerOperator<T extends NodeModel | NodeJson>(node?: T | null) {
  return isOperator(node, "power");
}

export function isSubscript<T extends NodeModel | NodeJson>(node?: T | null) {
  return isOperator(node, "subscript");
}

export function isFraction<T extends NodeModel | NodeJson>(node?: T | null) {
  return isOperator(node, "fraction");
}

export function isFractionDerivative(
  node?: NodeModel | null,
): node is Maybe<OperatorOf<NodeModel, "fraction">> {
  if (!isFraction(node)) return false;

  const [numerator, denominator] = node.childrenPlease;
  if (numerator?.type !== "inline" || denominator?.type !== "inline") return false;

  const numChildren = numerator.childrenPlease;
  const denChildren = denominator.childrenPlease;
  if (numChildren.length !== 1 || !isDifferential(numChildren[0])) return false;

  return (
    denChildren.length > 0 &&
    denChildren.every((child) => isDifferential(child) || isPowerOperator(child))
  );
}

export function isSupSubNode<T extends NodeModel | NodeJson>(node?: T | null) {
  return isPowerOperator(node) || isSubscript(node);
}

export function isLabel<T extends NodeModel | NodeJson>(
  node?: T | null,
): node is Extract<T, { type: "label" }> {
  return node?.type === "label";
}

export function isLabelOrSubscript<T extends NodeModel | NodeJson>(node?: T | null) {
  return isLabel(node) || isSubscript(node);
}
